/**
 * This file is part of ONP.
 *
 * Copyright (c) Opensoft (http://opensoftdev.com)
 *
 * The unauthorized use of this code outside the boundaries of
 * Opensoft is prohibited.
 *
 * @author Ivetta Tkachenko <ivetta.tkachenko@opensoftdev.ru>
 */

define(
    [
        'jquery',
        'underscore',
        'backbone',
        'assets/js/common/router/index',
        'assets/js/modules/designFacePreview/model/designFacePreviewModel',
        'assets/js/modules/designFacePreview/guideLines/renderer',
        'assets/js/modules/designFacePreview/mailingServicesGuides/renderer',
        'assets/js/modules/product/model/attributeModel',
        'assets/js/modules/option/optionModel',
        'assets/js/modules/option/optionValueModel'
    ],
    function(
        $,
        _,
        backbone,
        router,
        DesignFacePreviewModel,
        guideLinesRenderer,
        mailingServicesGuidesRenderer,
        AttributeModel,
        OptionModel,
        OptionValueModel
    ) {
        /**
         * Trim zone multiplier for preview. For posters and wrapping paper only
         * @type {number}
         */
        var TRIM_ZONE_RATIO = 1.044;

        /**
         * Short side multiplier when long side is changed
         * @type {number}
         */
        var PRODUCT_SIZE_RATIO = 1.0625;

        /**
         * @constructor
         * @param {object} options
         */
        var DesignFacePreview = function(options) {
            this.model = options.model;
            this.$target = options.$target;
            this.face = options.face;
            this.options = _.defaults(options.options || {}, this.defaultOptions);
        };

        DesignFacePreview.prototype = {
            selectorClassName: 'design-face-preview',
            trimmed: false,
            defaultOptions: {
                renderOptions: true,
                renderGuideLines: true,
                // support for postal mailing services zone guides
                renderMailingServicesGuides: false,
                placeholderColor: null,
                size: null
            },

            render: function(loadedImage) {
                if (loadedImage !== undefined) {
                    loadedImage = 'data:image/jpg;base64, ' + loadedImage;
                }
                var d = $.Deferred();
                this.$target.addClass(this.selectorClassName);

                this.renderImages(loadedImage).always(
                    function() {
                        this.renderContainer();
                        d.resolve();
                    }.bind(this)
                );

                return d.promise();
            },

            renderImages: function(loadedImage) {
                var _this = this;
                var deferreds = [];
                var $images = this.$target.find('img');
                var hasNoneTrimmedOption = this.model.hasOptionValue(OptionModel.NAME_TRIMMING, OptionValueModel.NAME_NONE_TRIMMING);

                $images.each(function() {
                    var d = $.Deferred();
                    var $img = $(this);
                    var src = loadedImage || $img.data('src') || $img.attr('src');

                    deferreds.push(d);

                    if (/\/low_/.test(src) && hasNoneTrimmedOption && _this.model.get('pgId')) {
                        src = router.generate('/dynamic-renderer/non-trimmed/{pgId}', { pgId: _this.model.get('pgId') });
                    }

                    $img.attr('src', src);

                    $img
                        .one('load', function() {
                            $img.removeAttr('data-src');
                            // make sure that container is stretched. The container may not be stretched if image has 'position: absolute'
                            // For example: on queryreview.php page if design has spot UV, spot UV image loads first and doesn't affect the size of the container
                            if (_this.$target.width() && _this.$target.height()) {
                                d.resolve();
                            }
                        })
                        .each(function() {
                            // jQuery onload has issues with browsers, so duplicating action here
                            if (this.complete) {
                                // make sure that container is stretched. The container may not be stretched if image has 'position: absolute'
                                // For example: on queryreview.php page if design has spot UV, spot UV image loads first and doesn't affect the size of the container
                                if (_this.$target.width() && _this.$target.height()) {
                                    if (_this.$target.hasClass('zoomed-img')) {
                                        $img.data('width', _this.$target.width());
                                        $img.data('height', _this.$target.height());
                                    }
                                    d.resolve();
                                }
                            }
                        });
                    $img.one('error', function() {
                        d.reject();
                    });
                });

                if (!$images.length) {
                    if (this.options.placeholderColor) {
                        this.$target.css('backgroundColor', this.options.placeholderColor);
                    }
                }

                return $.when.apply(null, deferreds);
            },

            renderContainer: function() {
                this.$target.removeClass(_.values(DesignFacePreviewModel.SHAPES).join(' '));
                this.$target.removeClass(_.values(DesignFacePreviewModel.OPTIONS).join(' '));

                if (this.options.renderOptions) {
                    if (this.model.get('shape') == AttributeModel.VALUE_SHAPE_CIRCLE) {
                        this.$target.addClass(DesignFacePreviewModel.SHAPES.CIRCLE);
                    }
                    if (this.model.hasOptionValue(OptionModel.NAME_HOLE_DRILLING, OptionValueModel.NAME_HOLE_DRILLING)) {
                        this.$target.addClass(DesignFacePreviewModel.OPTIONS.HOLE_DRILLED);
                    }
                    if (this.model.hasOptionValue(OptionModel.NAME_CORNERS, OptionValueModel.NAME_ROUNDED_CORNERS)) {
                        this.$target.addClass(DesignFacePreviewModel.OPTIONS.ROUNDED_CORNERS);
                    }
                }

                // US postal service guides for mail-sent items
                if (this.options.renderMailingServicesGuides) {
                    mailingServicesGuidesRenderer.render(this.model, this.$target, this.face);
                }

                const booklets = [
                    'booklets-85x11',
                    'booklets-55x85',
                    'booklets-a6-portrait',
                    'booklets-a6-landscape',
                    'booklets-a5-portrait',
                    'booklets-a5-landscape',
                    'booklets-a4-portrait'
                ];
                var face = this.$target.data('face');

                if (
                    this.model.has('flipbook') &&
                    this.model.get('flipbook').length > 0 &&
                    booklets.indexOf(this.model.get('productSlug')) >= 0
                ) {
                    if (this.$target.parents('.page').length) {
                        this.model.set('isEvenPage', this.$target.parents('.page').hasClass('even'));
                    } else {
                        this.model.set('isEvenPage', false);
                    }
                } else if (!this.$target.parent().hasClass(DesignFacePreviewModel.BOOKBLOCK.ITEM_CLASS) && face !== undefined) {
                    this.$target.css({
                        height: this.$target.find('img').height(),
                        width: this.$target.find('img').width()
                    });
                    this.model.set('isEvenPage', face % 2 === 0);
                } else if (
                    !this.$target.parent().hasClass(DesignFacePreviewModel.BOOKBLOCK.ITEM_CLASS) &&
                    this.$target.closest('.option-corners').length === 0
                ) {
                    this.$target.css(this.getImageSize());
                }

                this.model.set('width', this.$target.width());
                this.model.set('height', this.$target.height());
                this.model.set('isVertical', this.$target.width() < this.$target.height());
                this.changeContainerSize();

                if (this.options.renderGuideLines) {
                    guideLinesRenderer.render(this.model, this.$target, this.face);
                }

                this.hideLoading();

                return this;
            },

            getImageSize: function() {
                var $img = this.$target.find('img');
                var width = $img.data('width');
                var height = $img.data('height');

                if (!width || !height) {
                    width = $img.width();
                    height = $img.height();
                    $img.data('width', width);
                    $img.data('height', height);
                }

                return {
                    width: width,
                    height: height
                };
            },

            changeContainerSize: function() {
                var newLongSide, newShortSide;
                var longSide = this.model.get('isVertical') ? this.$target.height() : this.$target.width();
                var shortSide = this.model.get('isVertical') ? this.$target.width() : this.$target.height();

                // todo move this hardcode to config
                if (
                    this.options.size &&
                    this.model.hasOption(OptionModel.NAME_TRIMMING) &&
                    (this.model.get('productSlug') === 'pstr19x27' || this.model.get('productSlug') === 'wp20x28')
                ) {
                    if (this.model.get('options')[OptionModel.NAME_TRIMMING] == OptionValueModel.NAME_NONE_TRIMMING) {
                        newLongSide = this.options.size * (this.trimmed ? TRIM_ZONE_RATIO : 1);
                        this.trimmed = false;
                        if (newLongSide > longSide) {
                            newShortSide = shortSide * PRODUCT_SIZE_RATIO;
                        }
                    } else if (this.model.get('options')[OptionModel.NAME_TRIMMING] === OptionValueModel.NAME_TRIMMING) {
                        newLongSide = this.options.size;
                        this.trimmed = true;
                        if (newLongSide < longSide) {
                            newShortSide = shortSide / PRODUCT_SIZE_RATIO;
                        }
                    }
                }
                if (newLongSide && newShortSide) {
                    this.$target.css({
                        width: Math.round(this.model.get('isVertical') ? newShortSide : newLongSide),
                        height: Math.round(this.model.get('isVertical') ? newLongSide : newShortSide)
                    });
                }
            },

            /**
             * @param {DesignFacePreviewModel} model
             */
            setModel: function(model) {
                this.model = model;
            },

            /**
             * @param {string} optionName
             * @param {string} optionValue
             */
            setOption: function(optionName, optionValue) {
                this.model.setOption(optionName, optionValue);
            },

            hideLoading: function() {
                this.$target.removeClass('is-loading');
            },

            hideMailingGuides: function() {
                mailingServicesGuidesRenderer.hide(this.$target);
            },

            showMailingGuides: function() {
                mailingServicesGuidesRenderer.show(this.$target);
            }
        };

        return DesignFacePreview;
    }
);
